<template>
  <!-- Loading -->
  <mc-loading-indicator
    v-if="lclLoading && !firstLoad"
    class="card card--loading"
    data-test="lcl-bookings-loading"
    variant="spinner"
    appearance="primary"
    fit="large"
    :label="$t('common.loading')"
  />

  <div v-else data-test="lcl-bookings" class="air-lcl-bookings">
    <!-- Filters -->
    <div
      v-if="!(unconfirmedBookingsTab || cancelledBookingsTab)"
      data-test="air-lcl-shipments-filters"
      class="table-filters"
    >
      <TableFilters
        :loading="lclLoading"
        @update-component-time-frame="updateComponentTimeFrame"
        @reference-search="referenceSearch"
        @reset-reference-search="getLclBookingByStatus"
      />
      <JsonToCSV :filterValue="timeFrame" view-name="Lcl" :bookings="lclData" :pageNumber="lclPaginationToken" />
    </div>

    <!-- No results -->
    <MarketingNotification v-if="lclData && lclData.length === 0 && !lclError" class="card" data-test="no-results-lcl">
      <p
        tabindex="0"
        role="button"
        @keydown.enter="marketingClickThrough"
        @click="marketingClickThrough"
        v-html="
          unconfirmedBookingsTab
            ? $t('shipments.lclUnconfirmedNotification')
            : cancelledBookingsTab
              ? $t('shipments.lclCancelledNotification')
              : $t('shipments.lclNotification')
        "
      />

      <mc-button :label="$t('shipments.lclButton')" class="mt-2 mb-4" @click="navigateToLclBooking" />
    </MarketingNotification>

    <!-- Error -->
    <mc-notification
      v-else-if="lclData && lclData.length === 0 && lclError"
      class="card"
      width="fit-content"
      appearance="error"
      icon="exclamation-circle"
      data-test="error-lcl"
    >
      <span v-html="$t('datatable.error.noRecords500')"></span>
    </mc-notification>

    <!-- Results -->
    <mc-table
      v-else
      class="air-lcl__table"
      fit="small"
      sortdisabled
      :data.prop="lclData"
      :columns.prop="[
        {
          id: 'blNumber',
          label: firstColumnLabel,
          subDataLabel: firstColumnSubLabel,
          verticalAlign: 'top',
          width: '175px'
        },
        {
          id: 'from',
          label: $t('common.from'),
          subDataLabel: $t('tables.depPort'),
          verticalAlign: 'top'
        },
        {
          id: 'to',
          label: $t('common.to'),
          subDataLabel: $t('tables.arrPort'),
          verticalAlign: 'top'
        },
        {
          id: 'vessel',
          label: $t('common.vessel'),
          verticalAlign: 'top',
          width: '110px'
        },
        {
          id: 'cargo',
          label: $t('common.cargo'),
          verticalAlign: 'top'
        }
      ]"
      :data-test="`${$route?.path.split('/')[$route?.path.split('/').length - 1]}-table`"
    >
      <template v-for="(item, index) in lclData" :key="item.id">
        <div :slot="`${item.id}_blNumber`">
          <span v-if="cancelledBookingsTab"
            ><b>{{ item.tpDocNum || item.bookingReference }}</b></span
          >
          <template v-else>
            <a
              v-if="item.tpDocNum"
              :href="`/shipment-details/${item.tpDocNum}?mode=lcl`"
              class="color-primary"
              data-test="lcl-table-shipment-link"
              @click="
                $sendGAEvent({
                  category: 'SO_OutboundLink',
                  event: 'DQCS',
                  action: 'Click',
                  label: { data: 'SOLclSDLinkClick', isExport: !!isExport }
                })
              "
            >
              <b>{{ item.tpDocNum }}</b></a
            >
            <a
              v-else-if="item.bookingReference"
              :href="`/shipment-details/${item.bookingReference}?mode=lcl`"
              class="color-primary"
              data-test="lcl-table-shipment-link"
              @click="
                $sendGAEvent({
                  category: 'SO_LCLShipments',
                  event: 'DQCS',
                  action: 'Click',
                  label: {
                    data: 'soLclUnconfirmedBookingSDClick',
                    blNumber: item.bookingReference,
                    originCountryName: item?.placeOfReceipt.countryName || item?.portOfLoading.countryName,
                    destinationCountryName: item?.placeOfDelivery.countryName || item?.portOfDischarge.countryName
                  }
                })
              "
            >
              <b :class="{ 'unconfirmed-bookings-num-lcl': index === 0 }">{{ item.bookingReference }}</b></a
            >
          </template>
          <br />
          <CustomerReferences v-if="item.references" :references="item.references" />
        </div>
        <div :slot="`${item.id}_from`" :data-test="`lcl-table-row-from-${item.id + 1}`">
          <FromTo :place="item?.placeOfReceipt || item?.portOfLoading" :port="item?.portOfLoading" />
        </div>
        <div :slot="`${item.id}_to`" :data-test="`lcl-table-row-to-${item.id + 1}`">
          <FromTo
            :place="item?.placeOfDelivery || item?.portOfDischarge"
            :port="item?.portOfDischarge"
            type="arrival"
          />
        </div>
        <div :slot="`${item.id}_vessel`" :data-test="`lcl-table-row-vessel-${item.id + 1}`">
          <Vessel :vessel="item" />
        </div>
        <div :slot="`${item.id}_cargo`">
          <Cargo
            :commodity="item?.cargo?.commodity"
            :packageQuantity="item?.cargo?.packageQuantity"
            :packageQuantityUnit="item?.cargo?.packageQuantityUnit"
          />
        </div>
      </template>
    </mc-table>

    <Pagination
      v-if="lclData && lclData.length > 0 && lclLastPage > 1"
      activeView="lcl"
      :lastPage="lclLastPage"
      :totalCount="lclTotalCount"
      :currentPage="lclPaginationToken"
      @update-data-source="getData"
    />
  </div>
</template>
<script>
import MarketingNotification from "@/components/shared/MarketingNotification.vue";
import Pagination from "@/components/shared/Pagination.vue";
import { mapActions, mapState, mapWritableState } from "pinia";
import { filters } from "@/store/filters";
import { airLcl } from "@/store/airLcl";
import { dateFormat } from "@/lib/filters";
import { shipments } from "@/store/shipments";
import TableFilters from "@/components/filters/TableFilters.vue";
import CustomerReferences from "@/components/shared/CustomerReferences.vue";
import JsonToCSV from "@/components/shared/JsonToCSV.vue";
import FromTo from "@/components/shared/FromTo.vue";
import Cargo from "@/components/shared/Cargo.vue";
import Vessel from "@/components/shared/Vessel.vue";
import "@maersk-global/mds-components-core/mc-table";
import { showCoachMarks } from "@/lib/coachMarks.js";

export default {
  name: "Lcl",
  components: { Pagination, MarketingNotification, TableFilters, CustomerReferences, JsonToCSV, FromTo, Cargo, Vessel },
  data() {
    return {
      selectedRow: null,
      firstLoad: false,
      showCoachMarks
    };
  },
  computed: {
    ...mapWritableState(airLcl, [
      "lclData",
      "lclError",
      "lclLastPage",
      "lclTotalCount",
      "lclLoading",
      "lclPaginationToken"
    ]),
    ...mapState(filters, ["activeFilter"]),
    ...mapState(shipments, ["timeFrame", "refSearch"]),
    ...mapWritableState(shipments, ["timeFrame"]),
    isExport() {
      return this.$route?.path?.toLowerCase()?.includes?.("export");
    },
    unconfirmedBookingsTab() {
      return this.$route?.path?.includes("unconfirmedbookings");
    },
    cancelledBookingsTab() {
      return this.$route?.path?.includes("cancelledbookings");
    },
    firstColumnLabel() {
      return this.unconfirmedBookingsTab ? this.$t("common.bookingNumber") : this.$t("common.blNumber");
    },
    firstColumnSubLabel() {
      return this.unconfirmedBookingsTab ? this.$t("tables.customerReference") : this.$t("tables.references");
    },
    status() {
      if (this.unconfirmedBookingsTab) {
        return "unconfirmed";
      } else if (this.cancelledBookingsTab) {
        return "cancelled";
      } else {
        return "confirmed";
      }
    }
  },
  watch: {
    lclLoading: {
      handler() {
        this.firstLoad = true;
      }
    }
  },
  async mounted() {
    await this.getLclBookingByStatus();
    this.showNumCoachmark();
  },
  methods: {
    ...mapActions(airLcl, ["getLclBookings", "updateLclPaginationToken"]),
    ...mapActions(shipments, ["updateTimeFrame"]),
    dateFormat,
    showDetails(index) {
      if (this.selectedRow === index) {
        this.selectedRow = null;
      } else {
        this.selectedRow = index;
      }
    },
    async navigateToLclBooking() {
      this.$sendGAEvent({
        event: "DQCS",
        category: "Component",
        action: "Click",
        label: { data: "SOLclBookingClick" }
      });
      window.location.href = `/lcl`;
    },
    async updateComponentTimeFrame($event) {
      this.updateTimeFrame($event);
      this.timeFrame = $event.target.value;
      this.updateLclPaginationToken(1);
      await this.getLclBookingByStatus();
    },
    async getData(page) {
      this.updateLclPaginationToken(page);
      await this.getLclBookingByStatus();
    },
    async referenceSearch() {
      await this.getLclBookingByStatus();
      this.$sendGAEvent({
        event: "DQCS",
        category: "SO_RefSearch",
        action: "Click",
        label: { data: "soLclRefSearch", status: this.status }
      });
    },
    async getLclBookingByStatus() {
      if (this.unconfirmedBookingsTab) {
        await this.getLclBookings(this.isExport, "unconfirmed");
      } else if (this.cancelledBookingsTab) {
        await this.getLclBookings(this.isExport, "cancelled");
      } else {
        await this.getLclBookings(this.isExport, "confirmed");
      }
    },
    marketingClickThrough() {
      this.$sendGAEvent({
        category: "SO_OutboundLink",
        event: "DQCS",
        action: "Click",
        label: {
          data: "SOLclLinkClick",
          isExport: !!this.isExport
        }
      });
    },
    showNumCoachmark() {
      const seenCoachmark = Boolean(window.localStorage.getItem("so_unconfirmed_bookings_num_lcl_cm"));

      setTimeout(() => {
        if (document.querySelector(".unconfirmed-bookings-num-lcl")?.checkVisibility() && !seenCoachmark) {
          this.showCoachMarks(
            [
              {
                title: this.$t("shipments.coachMarks.unconfirmedBookingNumTitle"),
                text: this.$t("shipments.coachMarks.unconfirmedBookingNumText"),
                selector: ".unconfirmed-bookings-num-lcl",
                position: "right",
                seen: window.localStorage.setItem("so_unconfirmed_bookings_num_lcl_cm", "true")
              }
            ],
            this.$t("common.gotIt"),
            "soLclUnconfirmedBookingNumCoachmark"
          );
        }
      }, 1000);
    }
  }
};
</script>
